import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { LOGO_URL } from "@/data/content";
import { useLang, LANGUAGES } from "@/i18n";

const NAV = [
  { to: "/", key: "nav.home" },
  { to: "/chi-siamo", key: "nav.chiSiamo" },
  { to: "/#divisioni", key: "nav.businessUnit", anchor: "divisioni" },
  { to: "/magazine", key: "nav.magazine" },
  { to: "/contatti", key: "nav.contatti" },
];

const LangSwitcher = ({ mobile = false }) => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  if (mobile) {
    return (
      <div className="mt-8 flex items-center gap-3" data-testid="lang-switcher-mobile">
        <Globe size={15} className="text-[#C6A45D]" />
        {LANGUAGES.map((l) => (
          <button
            key={l.code}
            onClick={() => setLang(l.code)}
            data-testid={`lang-mobile-${l.code}`}
            className={`text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
              lang === l.code ? "text-[#E5C776] border-b border-[#C6A45D] pb-0.5" : "text-[#A5A5A5] hover:text-[#C6A45D]"
            }`}
          >
            {l.code}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative" data-testid="lang-switcher">
      <button
        onClick={() => setOpen(!open)}
        data-testid="lang-switcher-toggle"
        className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#F5F2EB]/80 hover:text-[#C6A45D] transition-colors duration-300 py-2"
        aria-label="Lingua"
      >
        <Globe size={14} />
        {lang.toUpperCase()}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25 }}
            className="absolute right-0 top-full mt-2 bg-[#141414] border border-[#C6A45D]/25 min-w-[150px] shadow-2xl shadow-black z-50"
          >
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); setOpen(false); }}
                data-testid={`lang-option-${l.code}`}
                className={`w-full text-left px-5 py-3 text-xs tracking-[0.2em] uppercase transition-colors duration-300 flex items-center justify-between gap-4 ${
                  lang === l.code ? "text-[#E5C776] bg-[#C6A45D]/10" : "text-[#A5A5A5] hover:text-[#F5F2EB] hover:bg-[#1D1D1D]"
                }`}
              >
                <span>{l.label}</span>
                <span className="text-[#C6A45D]/70">{l.code.toUpperCase()}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const goToAnchor = (e, item) => {
    if (!item.anchor) return;
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(item.anchor)?.scrollIntoView({ behavior: "smooth" });
      }, 400);
    } else {
      document.getElementById(item.anchor)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-[#080808]/90 backdrop-blur-md border-b border-[#C6A45D]/20"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 group">
            <img
              src={LOGO_URL}
              alt="Billions Group"
              className="h-11 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
            />
            <span className="hidden sm:block font-display text-sm tracking-[0.35em] text-[#F5F2EB] group-hover:text-[#C6A45D] transition-colors duration-300">
              BILLIONS <span className="text-[#C6A45D]">GROUP</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-10" data-testid="header-nav">
            {NAV.map((item) =>
              item.anchor ? (
                <a
                  key={item.key}
                  href={item.to}
                  data-testid={`nav-${item.key.split(".")[1].toLowerCase()}`}
                  onClick={(e) => goToAnchor(e, item)}
                  className="relative text-xs tracking-[0.25em] uppercase text-[#F5F2EB]/80 hover:text-[#C6A45D] transition-colors duration-300 py-2 after:absolute after:bottom-0 after:left-0 after:h-px after:w-0 after:bg-[#C6A45D] after:transition-[width] after:duration-300 hover:after:w-full"
                >
                  {t(item.key)}
                </a>
              ) : (
                <NavLink
                  key={item.key}
                  to={item.to}
                  data-testid={`nav-${item.key.split(".")[1].toLowerCase()}`}
                  className={({ isActive }) =>
                    `relative text-xs tracking-[0.25em] uppercase transition-colors duration-300 py-2 after:absolute after:bottom-0 after:left-0 after:h-px after:bg-[#C6A45D] after:transition-[width] after:duration-300 ${
                      isActive
                        ? "text-[#C6A45D] after:w-full"
                        : "text-[#F5F2EB]/80 hover:text-[#C6A45D] after:w-0 hover:after:w-full"
                    }`
                  }
                >
                  {t(item.key)}
                </NavLink>
              )
            )}
            <LangSwitcher />
            <Link
              to="/area-riservata"
              data-testid="area-riservata-button"
              className="ml-2 px-5 py-2.5 text-xs tracking-[0.25em] uppercase border border-[#C6A45D]/60 text-[#C6A45D] hover:bg-[#C6A45D] hover:text-[#080808] transition-colors duration-300"
            >
              {t("nav.areaRiservata")}
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="md:hidden text-[#F5F2EB] hover:text-[#C6A45D] transition-colors duration-300"
            aria-label="Apri menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-[#080808] flex flex-col items-center justify-center gap-2 md:hidden"
          >
            <img src={LOGO_URL} alt="" className="h-16 w-auto mb-10 opacity-90" />
            {NAV.map((item, i) => (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08, duration: 0.5 }}
              >
                {item.anchor ? (
                  <a
                    href={item.to}
                    data-testid={`mobile-nav-${item.key.split(".")[1].toLowerCase()}`}
                    onClick={(e) => goToAnchor(e, item)}
                    className="block font-display text-3xl text-[#F5F2EB] hover:text-[#C6A45D] transition-colors duration-300 py-3"
                  >
                    {t(item.key)}
                  </a>
                ) : (
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-${item.key.split(".")[1].toLowerCase()}`}
                    className={({ isActive }) =>
                      `block font-display text-3xl transition-colors duration-300 py-3 ${
                        isActive ? "text-[#C6A45D]" : "text-[#F5F2EB] hover:text-[#C6A45D]"
                      }`
                    }
                  >
                    {t(item.key)}
                  </NavLink>
                )}
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="flex flex-col items-center"
            >
              <Link
                to="/area-riservata"
                data-testid="mobile-area-riservata-button"
                className="mt-6 inline-block px-8 py-3 text-xs tracking-[0.3em] uppercase border border-[#C6A45D] text-[#C6A45D] hover:bg-[#C6A45D] hover:text-[#080808] transition-colors duration-300"
              >
                {t("nav.areaRiservata")}
              </Link>
              <LangSwitcher mobile />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
